'use client';

import cn from '@/utils/cn';
import * as ToastPrimitive from '@radix-ui/react-toast';
import { XCloseIcon } from '@tajdid/tajdid-ui/icons';
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';

type ToastVariant = 'success' | 'error' | 'warning';

type ToastInput = {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
};

type ToastItem = ToastInput & {
  id: string;
  open: boolean;
};

type ToastContextValue = {
  showToast: (input: ToastInput) => void;
};

const ToastContext = createContext<ToastContextValue>({
  showToast: () => undefined,
});

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}

const variantClass: Record<ToastVariant, string> = {
  success: 'border-success-300 bg-success-25 text-success-700',
  error: 'border-error-300 bg-error-25 text-error-700',
  warning: 'border-warning-300 bg-warning-25 text-warning-700',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const showToast = useCallback((input: ToastInput) => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { ...input, id, open: true }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      <ToastContext.Provider value={{ showToast }}>
        {children}
      </ToastContext.Provider>
      {toasts.map(toast => (
        <ToastPrimitive.Root
          key={toast.id}
          open={toast.open}
          duration={5000}
          onOpenChange={open => {
            if (!open) {
              dismiss(toast.id);
            }
          }}
          className={cn(
            'relative rounded p-4 w-80 md:w-[420px] shadow-lg border',
            variantClass[toast.variant ?? 'success'],
          )}
        >
          <div className="flex items-start gap-x-3 pr-6">
            <div className="w-full">
              {toast.title && (
                <ToastPrimitive.Title className="font-semibold">
                  {toast.title}
                </ToastPrimitive.Title>
              )}
              {toast.description && (
                <ToastPrimitive.Description className="text-sm font-normal">
                  {toast.description}
                </ToastPrimitive.Description>
              )}
            </div>
            <ToastPrimitive.Close className="absolute top-3 right-3">
              <XCloseIcon />
            </ToastPrimitive.Close>
          </div>
        </ToastPrimitive.Root>
      ))}
      <ToastPrimitive.Viewport className="fixed top-4 max-sm:top-20 right-2 md:right-4 flex flex-col-reverse gap-3 w-80 md:w-[420px] z-[9999]" />
    </ToastPrimitive.Provider>
  );
}
